// src/context/site/useKbStatus.js
//
// Polls GET /context/kb-status while the Knowledge Base page is open.
//
// Three things this hook deliberately does DIFFERENTLY from the documents
// poller in DocumentsView.jsx:
//   • it is UNBOUNDED — a reindex is budgeted at up to 30 minutes, so a 60 s
//     cutoff would silently freeze the chip;
//   • its cadence follows the chip: fast while a job runs (Discovering /
//     Indexing), slow otherwise;
//   • it is tab-aware: no fetch while document.hidden, one immediate fetch
//     when the tab becomes visible again.
//
// Everything the chip shows is derived on every read by the server. Nothing is
// cached here. ONE bounded exception (2026-09-17, after the first manual test):
// `markStarted(kind)` — called by the modal right after a trigger's 202 — shows
// the job's first stage AT ONCE and switches to the fast cadence, instead of
// leaving the page on "Pronto" until the next poll. The server does report a
// pending job as busy from its first answer, so the optimistic value is
// normally replaced within one tick; if the server keeps answering Ready it is
// dropped after OPTIMISTIC_MAX_READY answers (the job was too fast to see).
// Every other state change (a promote, a discard) still just calls refresh().
//
// `lastFinished` is set on a busy → not-busy transition so the card can say
// "completed at HH:MM" instead of silently flipping back to Pronto.
import { useState, useEffect, useCallback, useRef } from 'react';
import { getKbStatus } from '../contextApi.js';

/** The CLOSED vocabulary of `state` — branch on these exact strings, never invent a sixth. */
export const KB_STATES = ['Ready', 'Discovering', 'Indexing', 'Needs review', 'Failed'];

export const ACTIVE_INTERVAL_MS = 5000;
export const IDLE_INTERVAL_MS = 30000;

/** How many consecutive Ready answers the optimistic busy state survives after markStarted. */
export const OPTIMISTIC_MAX_READY = 3;

/** The two states during which PATCH /website-urls answers 409 KB_JOB_ACTIVE. */
export function isKbBusy(state) {
  return state === 'Discovering' || state === 'Indexing';
}

/** The stage a job of this kind starts in (mirrors the server's own pending rule). */
export function firstStageOf(kind) {
  return kind === 'reload' ? 'Discovering' : 'Indexing';
}

export default function useKbStatus({ tenantId, enabled = true }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [lastFinished, setLastFinished] = useState(null); // { kind, state, at }
  const timerRef = useRef(null);
  const lastStateRef = useRef(null);
  const tickRef = useRef(() => {});
  const optimisticRef = useRef(null); // { readyAnswers } while an optimistic busy state is held
  const prevRef = useRef(null);       // the last status the hook published

  useEffect(() => {
    if (!enabled || !tenantId) {
      setStatus(null);
      setError(null);
      setLastFinished(null);
      optimisticRef.current = null;
      prevRef.current = null;
      tickRef.current = () => {};
      return undefined;
    }
    let alive = true;
    const clear = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    const schedule = () => {
      clear();
      const delay = isKbBusy(lastStateRef.current) ? ACTIVE_INTERVAL_MS : IDLE_INTERVAL_MS;
      timerRef.current = setTimeout(() => tickRef.current(), delay);
    };
    const tick = async () => {
      if (!alive) return;
      if (typeof document !== 'undefined' && document.hidden) {
        schedule();
        return;
      }
      try {
        const next = await getKbStatus({ tenant: tenantId });
        if (!alive) return;
        const held = optimisticRef.current;
        if (held && !isKbBusy(next.state) && held.readyAnswers < OPTIMISTIC_MAX_READY) {
          // The job row is not visible to the reader yet: keep showing the
          // stage we announced, on the fast cadence, a little longer.
          held.readyAnswers += 1;
          setError(null);
          schedule();
          return;
        }
        optimisticRef.current = null;
        const prev = prevRef.current;
        if (prev && isKbBusy(prev.state) && !isKbBusy(next.state)) {
          setLastFinished({ kind: prev.kind, state: next.state, at: new Date().toISOString() });
        }
        prevRef.current = next;
        lastStateRef.current = next.state;
        setStatus(next);
        setError(null);
      } catch (e) {
        if (!alive) return;
        setError(e);
      }
      schedule();
    };
    const onVisibility = () => {
      if (!document.hidden) tick();
    };
    tickRef.current = tick;
    lastStateRef.current = null;
    optimisticRef.current = null;
    prevRef.current = null;
    document.addEventListener('visibilitychange', onVisibility);
    tick();
    return () => {
      alive = false;
      clear();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [tenantId, enabled]);

  const refresh = useCallback(() => tickRef.current(), []);

  const markStarted = useCallback((kind) => {
    const state = firstStageOf(kind);
    const optimistic = { state, kind, started_at: new Date().toISOString(), shadow_pending: null };
    optimisticRef.current = { readyAnswers: 0 };
    prevRef.current = optimistic;     // so the eventual busy → Ready transition is reported
    lastStateRef.current = state;     // fast cadence from now on
    setLastFinished(null);
    setStatus(optimistic);
    tickRef.current();
  }, []);

  const clearFinished = useCallback(() => setLastFinished(null), []);

  return {
    status,
    error,
    refresh,
    busy: isKbBusy(status?.state),
    markStarted,
    lastFinished,
    clearFinished,
  };
}
